/**
 * @module Messages
 * @description Messages page component for managing message categories and messages in the mental health portal.
 * Provides UI for creating, editing, and deleting both categories and messages.
 */

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  CircularProgress,
  IconButton,
  Popover,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ContainerPage from '../../components/ContainerPage/ContainerPage';
import Modal from '../../components/Resources/Modal/Modal';

import { useMessages } from '../../hooks/messages/useMessages';

import { useSendMessage } from '../../hooks/messages/useSendMessage';
import {
  ButtonStyled,
  CategoryDeleteSection,
  CategoryFormSection,
  CategoryItem,
  CategoryList,
  ConfirmSendMessageFormSection,
  ConfirmSendMessageTitle,
  HeaderContainer,
  HeaderSection,
  HeaderTitle,
  Main,
  MessageDeleteSection,
  MessageFormSection,
  MessageItem,
  MessageList,
  ModalHeader,
  PageTitle,
  PageTitleButton,
  SectionCategory,
  SectionContentMessage,
  SectionMessage,
  SendMessageFormSection,
  SendMessageTitle,
  StyledAutocomplete,
} from './Messages.Styles';

/**
 * @function Messages
 * @description Component for managing message categories and messages
 * @returns {JSX.Element} Messages page component
 */
const Messages = () => {
  const { t } = useTranslation();

  /**
   * @description Destructuring values and methods from useMessages hook
   */
  const {
    isLoading,
    selectMessage,
    selectedMessage,
    handleNewMessageForm,
    filteredMessages,
    isOpenMessageForm,
    handleEditMessageForm,
    formMessageData,
    updateFormMessageField,
    handleSaveMessageForm,
    handleUpdateMessageForm,
    handleCloseMessageForm,
    isOpenDeleteMessageModal,
    handleOpenDeleteMessageModal,
    handleCloseDeleteMessageModal,
    handlerDeleteMessageModal,
    errorsFormMessage,
    categories,
    selectCategory,
    selectedCategory,
    isOpenCategoryForm,
    handleNewCategoryForm,
    handleCloseCategoryFormModal,
    updateFormCategoryField,
    handleSaveCategoryForm,
    handleUpdateCategoryForm,
    formCategoryData,
    handleEditCategoryForm,
    isOpenDeleteCategoryForm,
    handleOpenDeleteCategoryFormModal,
    handleCloseDeleteCategoryFormModal,
    handleDeleteCategoryForm,
    errorsFormCategory,
    showAlert,
    handleCloseAlert,
    alertMessage,
    typeAlert,
  } = useMessages();

  const {
    isLoadingSendMessage,
    areas,
    employees,
    kpis,
    anchorEl,
    openSendMessage,
    handleOpenSendMessage,
    handleSendMessage,
    handleCloseSendMessage,
    messageData,
    updateTypeSend,
    updateFormSendMessageData,
    formSendMessageData,
    handleOpenConfirmSendMessage,
    handleCloseConfirmSendMessage,
    openConfirmSendMessage,
    anchorElConfirmSendMessage,
    errorsFormSendMessage,
    showAlertSendMessage,
    alertMessageSendMessage,
    typeAlertSendMessage,
    handleCloseAlertSendMessage,
  } = useSendMessage();

  const selectTextByTypeSend = (formSendMessageData) => {
    let text = '';
    if (formSendMessageData.type === 'area') {
      text = t('send message.all employees from the {{area}} department', {
        area: formSendMessageData.areaID ? formSendMessageData.areaID : '',
      });
    }

    if (formSendMessageData.type === 'employee') {
      text = t('send message.the employee {{employee_name}}', {
        employee_name: formSendMessageData.employee?.name
          ? formSendMessageData.employee?.name
          : '',
      });
    }

    if (formSendMessageData.type === 'kpi') {
      const kpiNameTranslation = t(formSendMessageData.kpi?.name);

      text = t(
        'send message.all employees with criterion {{kpi}} equal or above {{value}}',
        {
          kpi: kpiNameTranslation,
          value: formSendMessageData.kpiValue
            ? formSendMessageData.kpiValue
            : '',
        }
      );
    }

    return text;
  };

  /**
   * @function truncateDescription
   * @description Truncates a description text to a specified length and adds ellipsis
   * @param {string} description - The text to truncate
   * @param {number} [maxLength=25] - Maximum length before truncation
   * @returns {string} Truncated text with ellipsis if needed
   */
  const truncateDescription = (description, maxLength = 25) =>
    description && description.length > maxLength
      ? description.substring(0, maxLength) + '...'
      : description;

  /**
   * @constant {boolean} hasCategories
   * @description Checks if there are any categories available
   */
  const hasCategories = !!categories?.length;

  /**
   * @constant {boolean} hasFilteredMessages
   * @description Checks if there are any filtered messages available
   */
  const hasFilteredMessages = !!filteredMessages?.length;

  /**
   * @constant {boolean} hasSelectedCategory
   * @description Checks if a category is currently selected
   */
  const hasSelectedCategory = !!selectedCategory?.id;

  return (
    <ContainerPage>
      <PageTitle>
        {isOpenMessageForm ? (
          <>
            <PageTitleButton onClick={handleCloseMessageForm}>
              {'<'}
            </PageTitleButton>{' '}
            {formMessageData.id ? t('edit message') : t('new message')}
          </>
        ) : (
          t('messages')
        )}
      </PageTitle>

      {/* Alert notification */}
      <Snackbar
        open={showAlert}
        autoHideDuration={10000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          handleCloseAlert();
        }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={typeAlert}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showAlertSendMessage}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          handleCloseAlertSendMessage();
        }}
      >
        <Alert
          onClose={handleCloseAlertSendMessage}
          severity={typeAlertSendMessage}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessageSendMessage}
        </Alert>
      </Snackbar>

      <Main
        isLoading={isLoading}
        hasCategories={hasCategories}
        hasFilteredMessages={hasFilteredMessages}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {/* HEADER */}
            <HeaderContainer>
              <HeaderSection align="center" flexDirection="column">
                {!isOpenMessageForm && (
                  <>
                    <ButtonStyled onClick={handleNewCategoryForm}>
                      {t('create category')}
                    </ButtonStyled>

                    <ButtonStyled
                      onClick={handleNewMessageForm}
                      disabled={!hasCategories}
                    >
                      {t('new message')}
                    </ButtonStyled>
                  </>
                )}
              </HeaderSection>

              <HeaderSection align="space-between">
                {selectedCategory && (
                  <>
                    <HeaderTitle>{selectedCategory.name}</HeaderTitle>
                    <div className="buttons-card">
                      <button
                        onClick={() =>
                          handleEditCategoryForm(selectedCategory.id)
                        }
                      >
                        <EditIcon
                          htmlColor="rgba(0, 0, 0, 0.6)"
                          fontSize="small"
                        />
                      </button>
                      <button
                        onClick={handleOpenDeleteCategoryFormModal}
                        disabled={selectedCategory.amount > 0}
                      >
                        <DeleteIcon
                          htmlColor={
                            selectedCategory.amount > 0
                              ? 'rgba(0, 0, 0, 0.25)'
                              : 'rgba(0, 0, 0, 0.6)'
                          }
                          fontSize="small"
                        />
                      </button>
                    </div>
                  </>
                )}
              </HeaderSection>

              <HeaderSection align="space-between">
                {selectedMessage && (
                  <>
                    <HeaderTitle>{selectedMessage.name}</HeaderTitle>
                    <ButtonStyled
                      onClick={(event) => {
                        handleOpenSendMessage(event, selectedMessage);
                      }}
                    >
                      {t('Send')}
                    </ButtonStyled>
                  </>
                )}
              </HeaderSection>
            </HeaderContainer>

            <Popover
              open={openSendMessage}
              onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                  return;
                }

                handleCloseSendMessage();
              }}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 4,
                    mt: 1,
                    pointerEvents: openConfirmSendMessage ? 'none' : 'auto', // impede clique
                  },
                },
              }}
            >
              <SendMessageFormSection
                isLoadingSendMessage={isLoadingSendMessage}
              >
                {isLoadingSendMessage ? (
                  <CircularProgress />
                ) : (
                  <>
                    <div className="header">
                      <SendMessageTitle>
                        {t('send message.send message')}
                      </SendMessageTitle>
                      <IconButton onClick={handleCloseSendMessage}>
                        <CloseIcon />
                      </IconButton>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSendMessage(messageData);
                      }}
                    >
                      <label>
                        <input
                          id="area"
                          type="radio"
                          name="typeSend"
                          value="area"
                          checked={formSendMessageData.type === 'area'}
                          onChange={() =>
                            updateTypeSend('area', selectedMessage)
                          }
                          className={errorsFormSendMessage.type ? 'error' : ''}
                        />
                        <StyledAutocomplete
                          value={
                            areas.find(
                              (area) => area.id === formSendMessageData.areaID
                            ) || null
                          }
                          disabled={formSendMessageData.type !== 'area'}
                          onChange={(_, newValue) =>
                            updateFormSendMessageData(
                              'areaID',
                              newValue?.id || ''
                            )
                          }
                          options={areas}
                          getOptionLabel={(option) => option.id || ''}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          disableClearable
                          className={errorsFormSendMessage.area ? 'error' : ''}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={t('send message.area')}
                              error={!!errorsFormSendMessage.area}
                              size="small"
                            />
                          )}
                          maxwidth="20rem"
                          bgcolor="#3b86ff"
                          bgcoloractive="#3275e0"
                        />
                      </label>

                      <label>
                        <input
                          id="employee"
                          type="radio"
                          name="typeSend"
                          value="employee"
                          checked={formSendMessageData.type === 'employee'}
                          onChange={() =>
                            updateTypeSend('employee', selectedMessage)
                          }
                          className={errorsFormSendMessage.type ? 'error' : ''}
                        />
                        <StyledAutocomplete
                          value={formSendMessageData.employee || null}
                          disabled={formSendMessageData.type !== 'employee'}
                          onChange={(_, newValue) =>
                            updateFormSendMessageData('employee', newValue)
                          }
                          options={employees}
                          getOptionLabel={(option) => option.name || ''}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          disableClearable
                          className={
                            errorsFormSendMessage.employee ? 'error' : ''
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={t('send message.employee')}
                              error={!!errorsFormSendMessage.employee}
                              size="small"
                            />
                          )}
                          maxwidth="20rem"
                          bgcolor="#3b86ff"
                          bgcoloractive="#3275e0"
                        />
                      </label>

                      <div className="label-legend">
                        <label>
                          <input
                            id="kpi"
                            type="radio"
                            name="typeSend"
                            value="kpi"
                            checked={formSendMessageData.type === 'kpi'}
                            onChange={() =>
                              updateTypeSend('kpi', selectedMessage)
                            }
                            className={
                              errorsFormSendMessage.type ? 'error' : ''
                            }
                          />
                          <StyledAutocomplete
                            value={formSendMessageData.kpi || null}
                            disabled={formSendMessageData.type !== 'kpi'}
                            onChange={(_, newValue) =>
                              updateFormSendMessageData('kpi', newValue)
                            }
                            options={kpis.kpis}
                            getOptionLabel={(option) => t(option.name) || ''}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            disableClearable
                            className={errorsFormSendMessage.kpi ? 'error' : ''}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t('send message.criterion')}
                                error={!!errorsFormSendMessage.kpi}
                                size="small"
                              />
                            )}
                            maxwidth="20rem"
                            bgcolor="#3b86ff"
                            bgcoloractive="#3275e0"
                          />

                          <StyledAutocomplete
                            value={
                              formSendMessageData.kpiValue !== undefined &&
                              formSendMessageData.kpiValue !== ''
                                ? {
                                    id: Number(formSendMessageData.kpiValue),
                                    value: Number(formSendMessageData.kpiValue),
                                  }
                                : null
                            }
                            disabled={formSendMessageData.type !== 'kpi'}
                            onChange={(_, newValue) =>
                              updateFormSendMessageData(
                                'kpiValue',
                                newValue?.value !== undefined
                                  ? newValue.value.toString()
                                  : ''
                              )
                            }
                            options={Array.from(
                              { length: kpis.maxValue + 1 },
                              (_, i) => ({ id: Number(i), value: Number(i) })
                            )}
                            getOptionLabel={(option) => `${option.value}`}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            disableClearable
                            className={
                              errorsFormSendMessage.kpiValue ? 'error' : ''
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t('send message.average')}
                                error={!!errorsFormSendMessage.kpiValue}
                                size="small"
                              />
                            )}
                            maxwidth="6rem"
                            bgcolor="#3b86ff"
                            bgcoloractive="#3275e0"
                          />
                        </label>
                        <small>
                          {t('send message.equal to or greater than')}
                        </small>
                      </div>

                      <ButtonStyled
                        bgcolor="#5EE2A0"
                        bgcoloractive="#3cb67d"
                        maxwidth="100px"
                        className="button-send"
                        type="button"
                        disabled={false}
                        onClick={(e) => {
                          handleOpenConfirmSendMessage(e);
                        }}
                      >
                        {t('Send')}
                      </ButtonStyled>
                    </form>
                  </>
                )}
              </SendMessageFormSection>
            </Popover>

            <Popover
              open={openConfirmSendMessage}
              onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                  return;
                }

                handleCloseConfirmSendMessage();
              }}
              anchorEl={anchorElConfirmSendMessage}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 4,
                    mt: 4,
                  },
                },
              }}
            >
              <ConfirmSendMessageFormSection>
                <div className="header">
                  <ConfirmSendMessageTitle>
                    {t('send message.he is sure')}
                  </ConfirmSendMessageTitle>
                  <IconButton onClick={handleCloseConfirmSendMessage}>
                    <CloseIcon />
                  </IconButton>
                </div>
                <p>
                  {t(
                    'send message.are you sure you want to send this message to'
                  )}
                </p>

                <p className="bold">
                  {selectTextByTypeSend(formSendMessageData)}
                </p>

                <ButtonStyled
                  type="submit"
                  maxwidth="100px"
                  className="button-send"
                  onClick={() => {
                    handleSendMessage(formSendMessageData);
                  }}
                >
                  {t('Send')}
                </ButtonStyled>
              </ConfirmSendMessageFormSection>
            </Popover>

            {/* CATEGORY MODALS */}
            <Modal show={isOpenCategoryForm}>
              <ModalHeader>
                <span className="title-modal">
                  {formCategoryData.id
                    ? t('edit category')
                    : t('create category')}
                </span>
                <IconButton onClick={handleCloseCategoryFormModal}>
                  <CloseIcon />
                </IconButton>
              </ModalHeader>
              <CategoryFormSection>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();

                    if (formCategoryData.id) {
                      handleUpdateCategoryForm(formCategoryData);
                      return;
                    }

                    handleSaveCategoryForm(formCategoryData);
                  }}
                >
                  <input
                    type="text"
                    placeholder={t('category name')}
                    value={formCategoryData.name || ''}
                    onChange={(e) =>
                      updateFormCategoryField('name', e.target.value)
                    }
                    className={errorsFormCategory.name ? 'error' : ''}
                  />
                  <div className="buttons-area">
                    <ButtonStyled
                      type="button"
                      onClick={handleCloseCategoryFormModal}
                    >
                      {t('Cancel')}
                    </ButtonStyled>
                    <ButtonStyled type="submit">{t('Save')}</ButtonStyled>
                  </div>
                </form>
              </CategoryFormSection>
            </Modal>

            <Modal show={isOpenDeleteCategoryForm}>
              <ModalHeader>
                <span className="title-modal">{t('Delete Category')}</span>
                <IconButton onClick={handleCloseDeleteCategoryFormModal}>
                  <CloseIcon />
                </IconButton>
              </ModalHeader>
              <CategoryDeleteSection>
                <div>
                  <p>{t('alert-delete')}</p>
                </div>
                <div className="buttons-area">
                  <ButtonStyled onClick={handleCloseDeleteCategoryFormModal}>
                    {t('Cancel')}
                  </ButtonStyled>
                  <ButtonStyled
                    onClick={() =>
                      handleDeleteCategoryForm(selectedCategory.id)
                    }
                  >
                    {t('Confirm')}
                  </ButtonStyled>
                </div>
              </CategoryDeleteSection>
            </Modal>

            {hasCategories ? (
              <>
                {/* MESSAGE FORM */}
                {isOpenMessageForm ? (
                  <MessageFormSection>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();

                        if (formMessageData.id) {
                          handleUpdateMessageForm(formMessageData);
                          return;
                        }

                        handleSaveMessageForm(formMessageData);
                      }}
                    >
                      <select
                        value={formMessageData.category_id || ''}
                        onChange={(e) =>
                          updateFormMessageField('category_id', e.target.value)
                        }
                        className={errorsFormMessage.category_id ? 'error' : ''}
                      >
                        <option value="" disabled>
                          {t('Category')}
                        </option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        placeholder={t('message name')}
                        value={formMessageData.name || ''}
                        onChange={(e) =>
                          updateFormMessageField('name', e.target.value)
                        }
                        className={errorsFormMessage.name ? 'error' : ''}
                      />

                      <textarea
                        placeholder={t('content message')}
                        value={formMessageData.message || ''}
                        onChange={(e) =>
                          updateFormMessageField('message', e.target.value)
                        }
                        className={errorsFormMessage.message ? 'error' : ''}
                      />

                      <div className="buttons-area">
                        <ButtonStyled
                          type="button"
                          onClick={handleCloseMessageForm}
                        >
                          {t('Cancel')}
                        </ButtonStyled>
                        <ButtonStyled type="submit">{t('Save')}</ButtonStyled>
                      </div>
                    </form>
                  </MessageFormSection>
                ) : (
                  <>
                    {/* CATEGORY LIST */}
                    <SectionCategory>
                      <CategoryList>
                        {categories.map((category) => (
                          <CategoryItem
                            key={category.id}
                            isActive={selectedCategory?.id === category.id}
                            onClick={() => selectCategory(category.id)}
                          >
                            <span>{category.name}</span>
                            <span>{category.amount}</span>
                          </CategoryItem>
                        ))}
                      </CategoryList>
                    </SectionCategory>

                    {hasSelectedCategory ? (
                      hasFilteredMessages ? (
                        <>
                          {/* MESSAGE LIST */}
                          <SectionMessage>
                            <MessageList>
                              {filteredMessages.map((message) => (
                                <MessageItem
                                  key={message.id}
                                  isActive={selectedMessage?.id === message.id}
                                  onClick={() => selectMessage(message.id)}
                                >
                                  <div className="info-card">
                                    <span>{message.name}</span>
                                    <p>
                                      {truncateDescription(message.massage)}
                                    </p>
                                  </div>
                                  <div className="buttons-card">
                                    <button
                                      onClick={() =>
                                        handleEditMessageForm(message.id)
                                      }
                                    >
                                      <EditIcon
                                        htmlColor="rgba(0, 0, 0, 0.6)"
                                        fontSize="small"
                                      />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleOpenDeleteMessageModal(message.id)
                                      }
                                    >
                                      <DeleteIcon
                                        htmlColor="rgba(0, 0, 0, 0.6)"
                                        fontSize="small"
                                      />
                                    </button>
                                  </div>
                                </MessageItem>
                              ))}
                            </MessageList>
                          </SectionMessage>

                          {/* MESSAGE CONTENT */}
                          <SectionContentMessage>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedMessage?.message}
                            </Typography>
                          </SectionContentMessage>

                          {/* MESSAGE MODALS */}
                          <Modal show={isOpenDeleteMessageModal}>
                            <ModalHeader>
                              <span className="title-modal">
                                {t('delete message')}
                              </span>
                              <IconButton
                                onClick={handleCloseDeleteMessageModal}
                              >
                                <CloseIcon />
                              </IconButton>
                            </ModalHeader>
                            <MessageDeleteSection>
                              <div className="text-alert">
                                <p>{t('alert-delete-message')}</p>
                              </div>
                              <div className="buttons-area">
                                <ButtonStyled
                                  onClick={handleCloseDeleteMessageModal}
                                >
                                  {t('Cancel')}
                                </ButtonStyled>
                                <ButtonStyled
                                  onClick={() =>
                                    handlerDeleteMessageModal(
                                      selectedMessage.id
                                    )
                                  }
                                >
                                  {t('Confirm')}
                                </ButtonStyled>
                              </div>
                            </MessageDeleteSection>
                          </Modal>
                        </>
                      ) : (
                        <p> {t('no messages available')}</p>
                      )
                    ) : (
                      <p> {t('select category')}</p>
                    )}
                  </>
                )}
              </>
            ) : (
              <p>{t('no categories available')}</p>
            )}
          </>
        )}
      </Main>
    </ContainerPage>
  );
};

export default Messages;
